import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { EvidenceText } from "../../../api";
import { useAuth } from "../../../hooks";
import { useLanguage } from "../../../contexts";
import { isMaster } from "../../../utils/checkPermission";
import { highlight, countMatches, formatDate, moduleLabelKey, validityLabelKey, isImageFile, isPdfFile } from "./evidenceTextUtils";
import "./EvidenceTextViewer.scss";

const evidenceTextController = new EvidenceText();
const POLL_MS = 3000;

/**
 * Visor del texto que Sustemia leyó de un archivo de evidencia.
 * Se monta en document.body para poder abrirse encima de otros modales.
 */
export function EvidenceTextViewer({ open, onClose, module, fileKey, fileName, initialQuery = "" }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [query, setQuery] = useState(initialQuery);
  const [reprocessing, setReprocessing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const resolvedFileName = fileName || doc?.fileName || "";
  const isImage = isImageFile(resolvedFileName);
  const isPdf = isPdfFile(resolvedFileName);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const result = await evidenceTextController.getEvidenceText(accessToken, module, fileKey);
      setDoc(result);
      setNotFound(false);
    } catch (err) {
      if (err?.code === 404) setNotFound(true);
      else if (!silent) toast.error(t("ev_error_loading"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [accessToken, module, fileKey]);

  useEffect(() => {
    if (!open) return;
    setDoc(null);
    setNotFound(false);
    setQuery(initialQuery);
    load();
  }, [open, load, initialQuery]);

  // Mientras se está leyendo, refrescar cada unos segundos
  useEffect(() => {
    if (!open || doc?.status !== "pending") return;
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [open, doc?.status, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Vista previa embebida (imagen o PDF) del archivo original, no solo del
  // texto que se leyó de él.
  useEffect(() => {
    setPreviewUrl(null);
    setPreviewError(false);
    if (!open || (!isImage && !isPdf)) return;
    let cancelled = false;
    setPreviewLoading(true);
    evidenceTextController.getFile(accessToken, module, fileKey)
      .then((url) => {
        if (cancelled) { if (url) URL.revokeObjectURL(url); return; }
        if (url) setPreviewUrl(url); else setPreviewError(true);
      })
      .catch(() => { if (!cancelled) setPreviewError(true); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [open, isImage, isPdf, accessToken, module, fileKey]);

  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  if (!open) return null;

  // Word/Excel no se pueden mostrar embebidos en el navegador — se descargan
  // directo (con su nombre real) en vez de abrir una pestaña en blanco que
  // termina descargando igual. El texto leído, más abajo, sirve de vista previa.
  const handleOpenFile = async () => {
    setPreviewLoading(true);
    try {
      const url = await evidenceTextController.getFile(accessToken, module, fileKey);
      if (!url) { toast.error(t("ev_error_loading")); return; }
      const a = document.createElement("a");
      a.href = url;
      a.download = resolvedFileName || "";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("ev_error_loading"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      await evidenceTextController.reprocess(accessToken, module, fileKey);
      toast.success(t("ev_reprocess_queued"));
      setDoc((d) => (d ? { ...d, status: "pending" } : d));
    } catch {
      toast.error(t("ev_error_loading"));
    } finally {
      setReprocessing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(doc?.text || "");
      toast.success(t("ev_copied"));
    } catch { /* el navegador no permitió copiar */ }
  };

  const dates = doc?.dates || [];
  const latest = doc?.latestDate ? new Date(doc.latestDate).getTime() : null;
  const matches = query ? countMatches(doc?.text, query) : 0;
  // Si el archivo se subió sin nombre real, el backend guardó una referencia
  // legible (nombre del checklist, título del plan, etc.) para usar de título.
  const displayTitle = doc?.refLabel || resolvedFileName;

  return ReactDOM.createPortal(
    <div className="ev-viewer__overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ev-viewer" role="dialog" aria-modal="true" aria-label={t("ev_view_text")}>
        <div className="ev-viewer__header">
          <div className="ev-viewer__title">
            <Icon name="file alternate outline" />
            <div>
              <p className="ev-viewer__name" title={resolvedFileName}>{displayTitle}</p>
              <p className="ev-viewer__sub">
                {t(moduleLabelKey(module))}
                {doc?.refLabel && ` · ${resolvedFileName}`}
              </p>
            </div>
          </div>
          <button type="button" className="ev-viewer__close" onClick={onClose} aria-label={t("close")}>
            <Icon name="close" />
          </button>
        </div>

        <div className="ev-viewer__body">
          {loading ? (
            <p className="ev-viewer__empty"><Icon loading name="spinner" /> …</p>
          ) : notFound ? (
            <p className="ev-viewer__empty">{t("ev_not_read")}</p>
          ) : doc ? (
            <>
              <div className="ev-viewer__chips">
                <span className={`ev-chip ev-chip--${doc.status}`}>
                  {doc.status === "pending" && <Icon loading name="spinner" />}
                  {t(`ev_status_${doc.status}`)}
                </span>
                {doc.method && <span className="ev-chip">{t(`ev_method_${doc.method}`)}</span>}
                {doc.status === "done" && <span className="ev-chip">{(doc.text || "").length.toLocaleString("es-AR")} {t("ev_chars")}</span>}
              </div>

              <div className="ev-viewer__preview">
                {isImage ? (
                  previewLoading ? (
                    <div className="ev-viewer__preview-loading"><Icon loading name="spinner" /></div>
                  ) : previewUrl ? (
                    <img src={previewUrl} alt={resolvedFileName} className="ev-viewer__preview-img" />
                  ) : previewError ? (
                    <p className="ev-viewer__preview-error">{t("ev_preview_error")}</p>
                  ) : null
                ) : isPdf ? (
                  previewLoading ? (
                    <div className="ev-viewer__preview-loading"><Icon loading name="spinner" /></div>
                  ) : previewUrl ? (
                    <iframe src={previewUrl} title={resolvedFileName} className="ev-viewer__preview-pdf" />
                  ) : previewError ? (
                    <p className="ev-viewer__preview-error">{t("ev_preview_error")}</p>
                  ) : null
                ) : (
                  <button type="button" className="ev-btn ev-btn--ghost" onClick={handleOpenFile} disabled={previewLoading}>
                    <Icon name="download" /> {t("ev_open_file")}
                  </button>
                )}
              </div>

              {doc.status === "unsupported" && <p className="ev-viewer__notice">{t("ev_unsupported_hint")}</p>}
              {doc.status === "error" && <p className="ev-viewer__notice ev-viewer__notice--error">{t("ev_status_error")}{doc.error ? `: ${doc.error}` : ""}</p>}
              {doc.truncated && <p className="ev-viewer__notice">{t("ev_truncated")}</p>}

              {doc.status === "done" && doc.validity && (
                <div className={`ev-validity ev-validity--${doc.validity}`}>
                  <span className={`ev-chip ev-chip--validity-${doc.validity}`}>{t(validityLabelKey(doc.validity))}</span>
                  <span>
                    {doc.expiryDate
                      ? `${t(doc.validity === "expired" ? "ev_expired_on" : "ev_expires_on")} ${formatDate(doc.expiryDate)}`
                      : `${t("ev_doc_date")} ${formatDate(doc.docDate)} · ${t("ev_no_expiry")}`}
                  </span>
                </div>
              )}

              {dates.length > 0 && (
                <div className="ev-viewer__dates">
                  <span className="ev-viewer__label">{t("ev_dates_found")}:</span>
                  {dates.map((d) => {
                    const isLatest = new Date(d).getTime() === latest;
                    return (
                      <span key={d} className={`ev-date${isLatest ? " ev-date--latest" : ""}`} title={isLatest ? t("ev_latest") : undefined}>
                        {formatDate(d)}{isLatest ? ` · ${t("ev_latest")}` : ""}
                      </span>
                    );
                  })}
                </div>
              )}

              {doc.status === "done" && (
                doc.text ? (
                  <>
                    <div className="ev-viewer__find">
                      <Icon name="search" />
                      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("ev_find_in_text")} />
                      {query && <span className="ev-viewer__count">{matches} {t("ev_matches")}</span>}
                    </div>
                    <pre className="ev-viewer__text">{query ? highlight(doc.text, query) : doc.text}</pre>
                  </>
                ) : (
                  <p className="ev-viewer__empty">{t("ev_empty_text")}</p>
                )
              )}
            </>
          ) : null}
        </div>

        <div className="ev-viewer__footer">
          {doc && doc.status !== "pending" && (
            <button type="button" className="ev-btn ev-btn--ghost" onClick={handleReprocess} disabled={reprocessing}>
              <Icon name="redo" /> {t("ev_reprocess")}
            </button>
          )}
          {doc?.status === "done" && doc.text && (
            <button type="button" className="ev-btn ev-btn--ghost" onClick={handleCopy}>
              <Icon name="copy outline" /> {t("ev_copy")}
            </button>
          )}
          <button type="button" className="ev-btn" onClick={onClose}>{t("close")}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Botón chico para poner al lado de cada archivo adjunto: abre el texto leído.
 * `className` / `style` permiten igualar el estilo de los otros botones de la lista.
 */
export function EvidenceTextButton({ module, fileKey, fileName, className = "", style }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const { user } = useAuth();
  // Analizar evidencias es solo para master
  if (!fileKey || !isMaster(user?.role)) return null;

  return (
    <>
      <button
        type="button"
        className={`ev-text-btn ${className}`}
        style={style}
        title={t("ev_view_text")}
        aria-label={t("ev_view_text")}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <Icon name="file alternate outline" />
      </button>
      <EvidenceTextViewer open={open} onClose={close} module={module} fileKey={fileKey} fileName={fileName} />
    </>
  );
}
